/**
 * Auto-suggest column→field mappings by normalized-name similarity.
 *
 * Matches a CSV header against a field's `field_key`, its Persian label, and
 * its English label. Exact normalized match wins; otherwise a token-overlap
 * score with a threshold avoids confident-but-wrong guesses. Each field is
 * claimed at most once (greedy, best-score-first).
 */
import type { FieldDefinition } from '@/features/form-builder/api/form-builder-types'

export interface FieldChoice {
  fieldKey: string
  fieldType: string
  labelFa: string
  labelEn?: string
}

export function fieldChoices(fields: FieldDefinition[]): FieldChoice[] {
  return fields.map((f) => ({
    fieldKey: f.field_key,
    fieldType: f.field_type,
    labelFa: f.label?.fa ?? '',
    labelEn: f.label?.en,
  }))
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[‌‏]/g, '') // ZWNJ / RTL marks
    .replace(/[_\-/\\.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function tokens(s: string): string[] {
  return normalize(s).split(' ').filter(Boolean)
}

/** 0..1 similarity: 1 = exact normalized match, else Jaccard token overlap. */
function score(a: string, b: string): number {
  const na = normalize(a)
  const nb = normalize(b)
  if (!na || !nb) return 0
  if (na === nb) return 1
  const ta = new Set(tokens(a))
  const tb = new Set(tokens(b))
  if (ta.size === 0 || tb.size === 0) return 0
  let inter = 0
  for (const t of ta) if (tb.has(t)) inter++
  const union = new Set([...ta, ...tb]).size
  return inter / union
}

/**
 * Returns `column → fieldKey` suggestions. Greedy: highest-scoring pairs first,
 * each field used once. Pairs below `threshold` are dropped.
 */
export function suggestMappings(
  headers: string[],
  fields: FieldChoice[],
  threshold = 0.5,
): Record<string, string> {
  const pairs: Array<{ col: string; fieldKey: string; s: number }> = []
  for (const col of headers) {
    for (const f of fields) {
      const s = Math.max(
        score(col, f.fieldKey),
        score(col, f.labelFa),
        f.labelEn ? score(col, f.labelEn) : 0,
      )
      if (s >= threshold) pairs.push({ col, fieldKey: f.fieldKey, s })
    }
  }
  pairs.sort((a, b) => b.s - a.s)

  const out: Record<string, string> = {}
  const usedFields = new Set<string>()
  const usedCols = new Set<string>()
  for (const p of pairs) {
    if (usedCols.has(p.col) || usedFields.has(p.fieldKey)) continue
    out[p.col] = p.fieldKey
    usedCols.add(p.col)
    usedFields.add(p.fieldKey)
  }
  return out
}
